/**
 * DocConvert API - Word dönüştürme ve günlük not CRUD.
 */
import { apiRequest, API_BASE_URL, tokenManager } from './api';

/** img/audio etiketleri JWT header gönderemediği için token query param ekler. */
export const authMediaUrl = (url) => {
  if (!url) return url;
  const token = tokenManager.getAccessToken();
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
};

/** Günlük not görseli — her zaman API tabanlı URL (IIS /media uyumsuzluğu için). */
export const dailyNoteImageUrl = (imageId) => {
  if (!imageId) return '';
  return authMediaUrl(`${API_BASE_URL}/docconvert/daily-notes/images/${imageId}/`);
};

/** Günlük not dökümanı — indirme için API tabanlı URL. */
export const dailyNoteDocumentUrl = (documentId) => {
  if (!documentId) return '';
  return authMediaUrl(`${API_BASE_URL}/docconvert/daily-notes/documents/${documentId}/`);
};

export const downloadDailyNoteDocument = async (documentId, filename) => {
  const response = await fetch(dailyNoteDocumentUrl(documentId), {
    headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
  });
  if (!response.ok) {
    throw new Error(`İndirme başarısız oldu (HTTP ${response.status}).`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'dokuman';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const DAILY_NOTE_WRITE_FIELDS = [
  'ilgili_not_adi',
  'not_tarihi',
  'not_basligi',
  'not',
  'donusler',
  'ilgili_departman',
  'ilgili_kisi',
  'durum',
];

const appendDailyNoteFields = (formData, data = {}) => {
  DAILY_NOTE_WRITE_FIELDS.forEach((key) => {
    const value = data[key];
    const normalized = value == null ? '' : String(value);
    formData.append(key, normalized);
    if (key === 'not') {
      formData.append('note_content', normalized);
    }
  });
};

const buildDailyNoteFormData = (
  data,
  {
    noteImageFiles = [],
    returnImageFiles = [],
    noteDocumentFiles = [],
    returnDocumentFiles = [],
  } = {}
) => {
  const formData = new FormData();
  appendDailyNoteFields(formData, data);
  noteImageFiles.forEach((file, index) => formData.append(`note_image_${index}`, file));
  returnImageFiles.forEach((file, index) => formData.append(`return_image_${index}`, file));
  noteDocumentFiles.forEach((file, index) => formData.append(`note_document_${index}`, file));
  returnDocumentFiles.forEach((file, index) => formData.append(`return_document_${index}`, file));
  return formData;
};

const buildQuery = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const query = queryParams.toString();
  return query ? `?${query}` : '';
};

export const docConvertAPI = {
  convert: async (file, { saveNotes = false } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (saveNotes) formData.append('save_notes', 'true');
    return await apiRequest('/docconvert/convert/', 'POST', formData);
  },

  getModuleAccessUsers: async () => apiRequest('/docconvert/module-access/'),
  updateModuleAccessUsers: async (userIds) =>
    apiRequest('/docconvert/module-access/', 'PUT', { user_ids: userIds }),

  downloadExcel: async (conversionId, filename) => {
    const response = await fetch(`${API_BASE_URL}/docconvert/download/${conversionId}/`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
    });
    if (!response.ok) {
      throw new Error(`İndirme başarısız oldu (HTTP ${response.status}).`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'donusturulen.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  getDailyNotes: async (params = {}) =>
    apiRequest(`/docconvert/daily-notes/${buildQuery(params)}`),

  getDailyNote: async (id) => apiRequest(`/docconvert/daily-notes/${id}/`),

  getDailyNoteFilterOptions: async () =>
    apiRequest('/docconvert/daily-notes/filter-options/'),

  getMyNotes: async (params = {}) =>
    apiRequest(`/docconvert/daily-notes/my-notes/${buildQuery(params)}`),

  createDailyNote: async (
    data,
    {
      noteImageFiles = [],
      returnImageFiles = [],
      noteDocumentFiles = [],
      returnDocumentFiles = [],
    } = {}
  ) => {
    if (
      noteImageFiles.length > 0 ||
      returnImageFiles.length > 0 ||
      noteDocumentFiles.length > 0 ||
      returnDocumentFiles.length > 0
    ) {
      const formData = buildDailyNoteFormData(data, {
        noteImageFiles,
        returnImageFiles,
        noteDocumentFiles,
        returnDocumentFiles,
      });
      return apiRequest('/docconvert/daily-notes/', { method: 'POST', body: formData });
    }
    return apiRequest('/docconvert/daily-notes/', { method: 'POST', body: JSON.stringify(data) });
  },

  updateDailyNote: async (
    id,
    data,
    {
      noteImageFiles = [],
      returnImageFiles = [],
      noteDocumentFiles = [],
      returnDocumentFiles = [],
      removeImageIds = [],
      removeDocumentIds = [],
    } = {}
  ) => {
    if (
      noteImageFiles.length > 0 ||
      returnImageFiles.length > 0 ||
      noteDocumentFiles.length > 0 ||
      returnDocumentFiles.length > 0 ||
      removeImageIds.length > 0 ||
      removeDocumentIds.length > 0
    ) {
      const formData = buildDailyNoteFormData(data, {
        noteImageFiles,
        returnImageFiles,
        noteDocumentFiles,
        returnDocumentFiles,
      });
      if (removeImageIds.length > 0) {
        formData.append('remove_image_ids', removeImageIds.join(','));
      }
      if (removeDocumentIds.length > 0) {
        formData.append('remove_document_ids', removeDocumentIds.join(','));
      }
      return apiRequest(`/docconvert/daily-notes/${id}/`, { method: 'PATCH', body: formData });
    }
    return apiRequest(`/docconvert/daily-notes/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteDailyNote: async (id) =>
    apiRequest(`/docconvert/daily-notes/${id}/`, { method: 'DELETE' }),

  getNoteReturns: async (noteId) =>
    apiRequest(`/docconvert/daily-notes/${noteId}/returns/`),

  addNoteReturn: async (noteId, text) =>
    apiRequest(`/docconvert/daily-notes/${noteId}/returns/`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  updateNoteReturn: async (noteId, returnId, text) =>
    apiRequest(`/docconvert/daily-notes/${noteId}/returns/${returnId}/`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),

  deleteNoteReturn: async (noteId, returnId) =>
    apiRequest(`/docconvert/daily-notes/${noteId}/returns/${returnId}/`, {
      method: 'DELETE',
    }),

  bulkDeleteDailyNotes: async (ids) =>
    apiRequest('/docconvert/daily-notes/bulk-delete/', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  mergeDailyNotes: async (ids, targetId) =>
    apiRequest('/docconvert/daily-notes/merge/', {
      method: 'POST',
      body: JSON.stringify({ ids, target_id: targetId }),
    }),

  moveNotesToFolder: async (ids, folderId) =>
    apiRequest('/docconvert/daily-notes/move-to-folder/', {
      method: 'POST',
      body: JSON.stringify({ ids, folder_id: folderId }),
    }),

  getFolders: async (parentId) =>
    apiRequest(`/docconvert/folders/${buildQuery(parentId ? { parent: parentId } : {})}`),

  createFolder: async (name, parentId) =>
    apiRequest('/docconvert/folders/', {
      method: 'POST',
      body: JSON.stringify({ name, parent: parentId ?? null }),
    }),

  renameFolder: async (id, name) =>
    apiRequest(`/docconvert/folders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),

  deleteFolder: async (id) =>
    apiRequest(`/docconvert/folders/${id}/`, { method: 'DELETE' }),

  importConversionToDb: async (conversionId) =>
    apiRequest('/docconvert/daily-notes/import-conversion/', {
      method: 'POST',
      body: JSON.stringify({ conversion_id: conversionId }),
    }),

  bulkImportDailyNotes: async (rows) =>
    apiRequest('/docconvert/daily-notes/bulk-import/', {
      method: 'POST',
      body: JSON.stringify({ rows }),
    }),
};
